import { useEffect, useState } from 'react'
import { alpacaApi } from '../../api/alpaca'

type WsStatus = 'connecting' | 'connected' | 'disconnected' | 'error'

export function StatusBar() {
  const [time, setTime] = useState(new Date())
  const [wsStatus, setWsStatus] = useState<WsStatus | null>(null)
  const [alpacaConnected] = useState(() => alpacaApi.hasCredentials())

  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const handler = (e: Event) => {
      const status = (e as CustomEvent<{ status: WsStatus }>).detail.status
      setWsStatus(status)
    }
    window.addEventListener('bb:ws-status', handler)
    return () => window.removeEventListener('bb:ws-status', handler)
  }, [])

  const fmt = new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZoneName: 'short',
  })

  return (
    <footer
      role="contentinfo"
      aria-label="Status bar"
      className="flex items-center justify-between px-3 py-0.5 border-t border-bb-border bg-bb-surface shrink-0 text-2xs text-bb-disabled"
    >
      <span className="flex items-center gap-2">
        <span>QuantTradeAI © {new Date().getFullYear()}</span>
        <span role="status" aria-live="polite" aria-atomic="true" className="flex items-center gap-1">
          {wsStatus === 'connected' && (
            <span className="flex items-center gap-1 text-green-400 font-mono">
              <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              <span aria-label="Real-time data feed: live">LIVE</span>
            </span>
          )}
          {(wsStatus === 'connecting' || wsStatus === 'disconnected' || wsStatus === 'error') && (
            <span className="flex items-center gap-1 text-amber-400 font-mono">
              <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              <span aria-label="Real-time data feed: reconnecting">RECONNECTING</span>
            </span>
          )}
        </span>
      </span>
      <span className="hidden sm:flex items-center gap-3 text-bb-subtle" aria-hidden="true">
        <span><kbd className="font-mono text-bb-orange">/</kbd> search</span>
        <span><kbd className="font-mono text-bb-orange">1-9</kbd> pages</span>
        <span><kbd className="font-mono text-bb-orange">R M B V</kbd> indicators</span>
        <span><kbd className="font-mono text-bb-orange">[ ]</kbd> period</span>
        <span><kbd className="font-mono text-bb-orange">?</kbd> shortcuts</span>
      </span>
      <span className="flex items-center gap-3" aria-label="Data source status">
        <span className="flex items-center gap-1" title="Yahoo Finance: connected">
          <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-green-400" />
          <span className="text-bb-disabled" aria-label="Yahoo Finance connected">YF</span>
        </span>
        <span className="flex items-center gap-1" title={alpacaConnected ? 'Alpaca: connected' : 'Alpaca: not connected'}>
          <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full ${alpacaConnected ? 'bg-green-400' : 'bg-gray-500'}`} />
          <span className="text-bb-disabled" aria-label={alpacaConnected ? 'Alpaca connected' : 'Alpaca not connected'}>ALP</span>
        </span>
      </span>
      <span className="text-bb-subtle" aria-label={`Current time: ${fmt.format(time)}`}>{fmt.format(time)}</span>
    </footer>
  )
}
